import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDTypography from "../../../components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// NewProduct page components
import NewsletterInfo from "./components/NewsletterInfo";
import newsletterService from "../../../services/newsletterService";

// Esquema de validación con Yup
const newsletterSchema = yup.object().shape({
  email: yup.string().required("El email es obligatorio").email("Formato de email inválido"),
  status: yup.string().required("El estado es obligatorio"),
  sections: yup.array().of(yup.string()),
});

const steps = ["1. Sobre el newsletter"];

function NewNewsletter() {
  let navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(newsletterSchema),
    mode: "onChange",
    context: { isEdit: !!id },
    defaultValues: {
      email: "",
      status: "activo",
      sections: [],
    },
  });

  useEffect(() => {
    const fetchNewNewsletter = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data } = await newsletterService.getById(id);
        const newsletter = data;

        Object.keys(newsletter).forEach((key) => {
          const value = newsletter[key] || "";
          setValue(key, value);
        });
      } catch (error) {
        console.error("Error cargando Newsletters:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewNewsletter();
  }, [id, setValue]);

  const handleNext = async () => {
    const fieldsToValidate =
      activeStep === 0
        ? ["email"]
        : [];

    const result = await trigger(fieldsToValidate);

    if (result) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!id) {
        await newsletterService.create(data);
      } else {
        await newsletterService.update(id, data);
      }
      navigate("/newsletters");
    } catch (error) {
      console.error("Error guardando newsletter:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar. Revisa la consola.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                {id ? "Editar" : "Registrar nuevo"} newsletter
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                Información del newsletter
              </MDTypography>
            </MDBox>

            <Card>
              <MDBox mt={-3} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>

              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {activeStep === 0 && (
                    <NewsletterInfo control={control} errors={errors} isEdit={!!id} />
                  )}

                  <MDBox mt={4} display="flex" justifyContent="space-between">
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="light" onClick={handleBack}>
                        Atrás
                      </MDButton>
                    )}

                    <MDBox display="flex" flexDirection="column" alignItems="center">
                      <MDTypography variant="button" fontWeight="medium" color="text">
                        Resumen:
                      </MDTypography>
                      <MDTypography variant="caption" color="secondary">
                        {control._formValues.sections?.length || 0} secciones seleccionadas | Estado: {control._formValues.status}
                      </MDTypography>
                    </MDBox>

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={isLastStep ? handleSubmit(onSubmit) : handleNext}
                      disabled={isLoading}
                    >
                      {isLoading ? "Guardando..." : isLastStep ? "Guardar" : "Siguiente"}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewNewsletter;
